import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, X } from "lucide-react";
import { api, formatApiError } from "../lib/api";

const WILLING = ["Sydney", "Melbourne", "Brisbane", "Perth", "Adelaide", "Gold Coast", "Regional", "Remote"];
const EMP_TYPES = ["Full-time", "Part-time", "Contract", "Casual"];
const YEARS = ["0–2", "3–5", "6–10", "10+"];
const NOTICE = ["Immediately", "1 week", "2 weeks", "4 weeks", "2 months", "2+ months"];
const RIGHTS = ["Australian Citizen", "Permanent Resident", "Visa holder", "No current working rights"];

const inputCls = "mt-1.5 w-full rounded-lg border border-[#e3e0d3] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#94a01a]";
const labelCls = "text-xs uppercase tracking-[0.15em] font-medium text-[#51637a]";

const Section = ({ n, title, children }) => (
  <section className="rounded-2xl border border-[#e3e0d3] bg-white p-6" data-testid={`intake-section-${n}`}>
    <div className="mb-5 flex items-center gap-3">
      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#0d2f4d] text-xs font-bold text-[#C1CC24]">{n}</span>
      <h3 className="font-heading text-lg font-bold text-[#0d2f4d]">{title}</h3>
    </div>
    <div className="space-y-5">{children}</div>
  </section>
);
const Chips = ({ options, value, onToggle, testid }) => (
  <div className="flex flex-wrap gap-2" data-testid={testid}>
    {options.map((o) => {
      const on = value.includes(o);
      return (
        <button type="button" key={o} onClick={() => onToggle(o)}
          className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${on ? "border-[#0d2f4d] bg-[#0d2f4d] text-[#C1CC24]" : "border-[#e3e0d3] bg-white text-[#0d2f4d] hover:border-[#0d2f4d]"}`}>
          {o}
        </button>
      );
    })}
  </div>
);
const YesNo = ({ value, onChange, testid }) => (
  <div className="mt-1.5 flex gap-2" data-testid={testid}>
    {[["Yes", true], ["No", false]].map(([lbl, val]) => (
      <button type="button" key={lbl} onClick={() => onChange(val)} data-testid={`${testid}-${lbl.toLowerCase()}`}
        className={`rounded-full border px-6 py-2 text-sm font-medium ${value === val ? "border-[#0d2f4d] bg-[#0d2f4d] text-[#C1CC24]" : "border-[#e3e0d3] bg-white text-[#0d2f4d] hover:border-[#0d2f4d]"}`}>{lbl}</button>
    ))}
  </div>
);

export function CompleteIntakeModal({ onClose, onDone, initial = {} }) {
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    mobile: initial.mobile || "", website: "",
    current_location: initial.location || "", target_location: "", relocating: null,
    work_rights_status: "", visa_type: "", visa_expiry: "",
    current_job_title: "", industry: "", years_experience: "", roles: "",
    willing_to_work: [], employment_type: [], salary_range: "", notice_period: "", start_date: "", actively_seeking: null,
    cv_base64: "", cv_filename: "", linkedin_url: "", career_goals: "", non_negotiables: "", notes_before_call: "",
    social_media_consent: null,
    consent_storage: false, consent_no_guarantee: false, consent_approval: false,
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggle = (k, o) => setF((p) => ({ ...p, [k]: p[k].includes(o) ? p[k].filter((x) => x !== o) : [...p[k], o] }));
  const onCv = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error("CV must be under 10MB");
    const r = new FileReader();
    r.onload = () => setF((p) => ({ ...p, cv_base64: r.result, cv_filename: file.name }));
    r.readAsDataURL(file);
  };

  const submit = async () => {
    if (!f.work_rights_status) return toast.error("Please tell us your Australian work rights / visa status");
    if (!(f.consent_storage && f.consent_no_guarantee && f.consent_approval))
      return toast.error("Please tick all three consent boxes");
    setBusy(true);
    try {
      await api.post("/candidate/complete-intake", f);
      toast.success("Thanks — your profile has been updated");
      onDone?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 py-10" data-testid="complete-intake-modal">
      <div className="relative w-full max-w-2xl rounded-2xl bg-[#f5f3ee] p-6 sm:p-8">
        <button onClick={onClose} data-testid="intake-modal-close"
          className="absolute right-4 top-4 rounded-full p-1.5 text-[#51637a] hover:bg-[#e3e0d3]"><X size={18} /></button>
        <header className="mb-6 pr-8">
          <p className="font-accent text-lg text-[#7C8514]">Candidate Discovery Form</p>
          <h2 className="mt-1 font-heading text-2xl font-bold tracking-tight text-[#0d2f4d]">Complete your profile</h2>
          <p className="mt-2 text-sm text-[#51637a]">We've refreshed our candidate profile. Completing these helps our recruiters represent you properly. Registration is always free.</p>
        </header>

        <div className="space-y-5">
          <Section n={1} title="About you">
            <div><label className={labelCls}>Phone</label><input className={inputCls} data-testid="ci-mobile" value={f.mobile} onChange={(e) => set("mobile", e.target.value)} /></div>
            <div><label className={labelCls}>Current location</label><input className={inputCls} data-testid="ci-location" value={f.current_location} onChange={(e) => set("current_location", e.target.value)} placeholder="e.g. Bondi, NSW" /></div>
            <div><label className={labelCls}>Are you relocating to Australia?</label><YesNo testid="ci-relocating" value={f.relocating} onChange={(v) => set("relocating", v)} /></div>
          </Section>

          <Section n={2} title="Work rights & target location">
            <div>
              <label className={labelCls}>Australian work rights / visa status</label>
              <select className={inputCls} data-testid="ci-rights" value={f.work_rights_status} onChange={(e) => set("work_rights_status", e.target.value)}>
                <option value="">Select…</option>{RIGHTS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            {f.work_rights_status === "Visa holder" && (
              <div className="grid gap-4 sm:grid-cols-2" data-testid="ci-visa-conditional">
                <div><label className={labelCls}>Visa type</label><input className={inputCls} data-testid="ci-visa-type" value={f.visa_type} onChange={(e) => set("visa_type", e.target.value)} /></div>
                <div><label className={labelCls}>Visa expiry</label><input type="date" className={inputCls} data-testid="ci-visa-expiry" value={f.visa_expiry} onChange={(e) => set("visa_expiry", e.target.value)} /></div>
              </div>
            )}
            <div><label className={labelCls}>Australian city or state you're targeting</label><input className={inputCls} data-testid="ci-target" value={f.target_location} onChange={(e) => set("target_location", e.target.value)} placeholder="e.g. Sydney, NSW" /></div>
          </Section>

          <Section n={3} title="Your experience">
            <div className="grid gap-4 sm:grid-cols-2">
              <div><label className={labelCls}>Current job title</label><input className={inputCls} data-testid="ci-jobtitle" value={f.current_job_title} onChange={(e) => set("current_job_title", e.target.value)} /></div>
              <div><label className={labelCls}>Industry</label><input className={inputCls} data-testid="ci-industry" value={f.industry} onChange={(e) => set("industry", e.target.value)} placeholder="e.g. Hospitality" /></div>
            </div>
            <div><label className={labelCls}>Years of experience</label><div className="mt-1.5"><Chips testid="ci-years" options={YEARS} value={f.years_experience ? [f.years_experience] : []} onToggle={(o) => set("years_experience", f.years_experience === o ? "" : o)} /></div></div>
            <div><label className={labelCls}>Roles you're interested in</label><input className={inputCls} data-testid="ci-roles" value={f.roles} onChange={(e) => set("roles", e.target.value)} placeholder="e.g. Head Chef, Sous Chef" /></div>
          </Section>

          <Section n={4} title="What you're looking for">
            <div><label className={labelCls}>Preferred location</label><div className="mt-1.5"><Chips testid="ci-willing" options={WILLING} value={f.willing_to_work} onToggle={(o) => toggle("willing_to_work", o)} /></div></div>
            <div><label className={labelCls}>Employment type</label><div className="mt-1.5"><Chips testid="ci-emptype" options={EMP_TYPES} value={f.employment_type} onToggle={(o) => toggle("employment_type", o)} /></div></div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div><label className={labelCls}>Salary expectations</label><input className={inputCls} data-testid="ci-salary" value={f.salary_range} onChange={(e) => set("salary_range", e.target.value)} placeholder="e.g. $85k–$95k + super" /></div>
              <div><label className={labelCls}>Notice period</label><select className={inputCls} data-testid="ci-notice" value={f.notice_period} onChange={(e) => set("notice_period", e.target.value)}><option value="">Select…</option>{NOTICE.map((n) => <option key={n} value={n}>{n}</option>)}</select></div>
              <div><label className={labelCls}>Earliest available start date</label><input type="date" className={inputCls} data-testid="ci-startdate" value={f.start_date} onChange={(e) => set("start_date", e.target.value)} /></div>
            </div>
            <div><label className={labelCls}>Are you actively seeking employment?</label><YesNo testid="ci-seeking" value={f.actively_seeking} onChange={(v) => set("actively_seeking", v)} /></div>
          </Section>

          <Section n={5} title="Your CV & goals">
            <div>
              <label className={labelCls}>CV upload (max 10MB)</label>
              <label className="mt-1.5 flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-[#cfcbbc] bg-white px-4 py-3 text-sm text-[#51637a] hover:border-[#0d2f4d]">
                <Upload className="h-4 w-4" /> {f.cv_filename || "Choose file (PDF/DOC)"}
                <input type="file" accept=".pdf,.doc,.docx" className="hidden" data-testid="ci-cv" onChange={onCv} />
              </label>
            </div>
            <div><label className={labelCls}>LinkedIn profile</label><input className={inputCls} data-testid="ci-linkedin" value={f.linkedin_url} onChange={(e) => set("linkedin_url", e.target.value)} placeholder="https://linkedin.com/in/…" /></div>
            <div><label className={labelCls}>Career goals</label><textarea rows={4} className={inputCls} data-testid="ci-goals" value={f.career_goals} onChange={(e) => set("career_goals", e.target.value)} /></div>
            <div><label className={labelCls}>What matters most to you in your next role?</label><textarea rows={3} className={inputCls} data-testid="ci-matters" value={f.non_negotiables} onChange={(e) => set("non_negotiables", e.target.value)} /></div>
            <div><label className={labelCls}>Any additional information</label><textarea rows={3} className={inputCls} data-testid="ci-notes" value={f.notes_before_call} onChange={(e) => set("notes_before_call", e.target.value)} /></div>
            <input type="text" tabIndex={-1} autoComplete="off" value={f.website} onChange={(e) => set("website", e.target.value)} className="hidden" aria-hidden="true" data-testid="ci-hp" />
          </Section>

          <Section n={6} title="Profile visibility (optional)">
            <label className="text-sm font-medium text-[#0d2f4d]">Would you be happy for The Ditch Careers to advertise an anonymised version of your candidate profile on our social media and employer channels?</label>
            <YesNo testid="ci-social" value={f.social_media_consent} onChange={(v) => set("social_media_consent", v)} />
            <p className="mt-3 text-xs text-[#51637a] leading-relaxed">This is optional and will not affect your eligibility for roles. Your surname, contact details and identifying information will not be included without your further permission.</p>
          </Section>

          <div className="space-y-3 rounded-2xl border border-[#e3e0d3] bg-white p-6">
            {[["consent_storage", "I consent to The Ditch Careers storing and processing my data."],
              ["consent_no_guarantee", "I understand placement is not guaranteed."],
              ["consent_approval", "I understand my profile will be approved by me before being presented to any employer."]].map(([k, label]) => (
              <label key={k} className="flex items-start gap-3 text-sm text-[#33475b]">
                <input type="checkbox" data-testid={`ci-consent-${k}`} checked={f[k]} onChange={(e) => set(k, e.target.checked)} className="mt-1 h-4 w-4 accent-[#94a01a]" />
                <span>{label}</span>
              </label>
            ))}
          </div>

          <button onClick={submit} disabled={busy} data-testid="ci-submit"
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#C1CC24] px-8 py-4 text-base font-bold text-[#0d2f4d] transition-colors hover:bg-[#aeb81f] disabled:opacity-60">
            {busy && <Loader2 className="h-5 w-5 animate-spin" />} Save my profile
          </button>
        </div>
      </div>
    </div>
  );
}
